import React, { useRef, useState } from 'react';
import { Alert, Checkbox, Form, Input, Modal } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/api/queryKeys';
import {
  ProductImageApiError,
  productImages,
} from '@/api/ProductImages/ProductImages';
import type {
  GenerateProductImageRequest,
  ProductImageSummary,
} from '@/api/ProductImages/types';
import { useTranslate } from '@/translations';

export const GenerateImageDialog = ({
  product,
  onClose,
}: {
  product: ProductImageSummary;
  onClose: () => void;
}) => {
  const { t } = useTranslate();
  const client = useQueryClient();
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<number | null>(null);
  const request = useRef<GenerateProductImageRequest | null>(null);
  const unknown =
    product.latestGeneration?.status === 'Unknown' ||
    product.generationStatus === 'Unknown';
  const submit = async () => {
    if (submitting || !reason.trim() || (unknown && !acknowledged)) return;
    request.current ??= {
      idempotencyKey: crypto.randomUUID(),
      expectedCurrentRevisionId:
        product.currentRevisionId === undefined
          ? (product.currentImage?.assetId ?? null)
          : product.currentRevisionId,
      reason: reason.trim(),
      acknowledgeUnknownOutcome: acknowledged,
    };
    setSubmitting(true);
    setError(null);
    try {
      await productImages.generate(product.productId, request.current);
      await client.invalidateQueries({ queryKey: [QUERY_KEYS.productImagery] });
      onClose();
    } catch (failure) {
      setError(failure instanceof ProductImageApiError ? failure.status : 500);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Modal
      open
      centered
      title={t('imagery.confirmTitle', { product: product.productCode })}
      onCancel={submitting ? undefined : onClose}
      onOk={() => void submit()}
      confirmLoading={submitting}
      okText={t('imagery.confirm')}
      cancelText={t('imagery.cancel')}
      okButtonProps={{
        disabled:
          !reason.trim() ||
          (unknown && !acknowledged) ||
          error === 409 ||
          error === 403,
      }}
      destroyOnHidden
    >
      <p>{t('imagery.fullGeneration')}</p>
      <p>
        {t(
          product.currentImage
            ? 'imagery.confirmReplacement'
            : 'imagery.confirmNew',
        )}
      </p>
      <p>{t('imagery.confirmQuota')}</p>
      {unknown ? (
        <Alert
          type="warning"
          showIcon
          message={t('imagery.unknownWarning')}
          className="mb-3"
        />
      ) : null}
      <Form layout="vertical">
        <Form.Item
          label={t('imagery.reason')}
          htmlFor="imagery-generation-reason"
          required
        >
          <Input.TextArea
            id="imagery-generation-reason"
            autoFocus
            value={reason}
            maxLength={500}
            rows={3}
            disabled={!!request.current}
            onChange={(event) => setReason(event.target.value)}
          />
        </Form.Item>
        {unknown ? (
          <Checkbox
            checked={acknowledged}
            disabled={!!request.current}
            onChange={(event) => setAcknowledged(event.target.checked)}
          >
            {t('imagery.unknownAcknowledge')}
          </Checkbox>
        ) : null}
      </Form>
      {error ? (
        <Alert
          className="mt-3"
          type="error"
          showIcon
          message={t(
            error === 409
              ? 'imagery.requestConflict'
              : error === 403
                ? 'imagery.requestDenied'
                : 'imagery.requestFailed',
          )}
        />
      ) : null}
    </Modal>
  );
};
